import React from 'react';
import { UserModel } from '../types';

export interface AppState {
  user: UserModel | undefined;
  status: Status;
}

type Status =
  | 'LOADING'
  | 'AUTHENTICATING_USER'
  | 'USER_AUTHENTICATED'
  | 'AUTHENTICATION_FAILED';

type AppAction =
  | { type: 'START_AUTHENTICATION' }
  | { type: 'END_AUTHENTICATION' }
  | { type: 'SET_USER'; user: UserModel | undefined };

type AppDispatch = (action: AppAction) => void;

const initialState: AppState = {
  user: undefined,
  status: 'LOADING',
};

const reducer = (state: AppState, action: AppAction) => {
  switch (action.type) {
    case 'SET_USER':
      return {
        ...state,
        user: action.user,
      } as AppState;
    default:
      throw new Error();
  }
};

const AppStateContext = React.createContext<AppState>(initialState);
const AppDispatchContext = React.createContext<AppDispatch | undefined>(
  undefined
);

const AppProvider: React.FC = ({ children }) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);

  return (
    <AppStateContext.Provider value={state}>
      <AppDispatchContext.Provider value={dispatch}>
        {children}
      </AppDispatchContext.Provider>
    </AppStateContext.Provider>
  );
};

function useAppState() {
  const context = React.useContext(AppStateContext);
  if (context === undefined) {
    throw new Error('useAppState must be used within a AppProvider');
  }
  return context;
}

function useAppDispatch() {
  const context = React.useContext(AppDispatchContext);
  if (context === undefined) {
    throw new Error('useAppDispatch must be used within a AppProvider');
  }
  return context;
}

export { AppProvider, useAppState, useAppDispatch };
